import React, { useMemo, useState, useEffect } from 'react';
import { Typography, TextField, Autocomplete, Icon, SelectMenu, Button } from '@scale/scaleui';
import { GreenhouseJob, ResumeScreeningConfig, PodType, ScreenType } from '@scale/shared-types';
import _ from 'lodash';
import { RadioGroup, Radio } from 'design-system';
import { screenGreenhouseJobBacklog } from 'react-scale/corp/lib/api/growth';

export const ImportGreenhouseJob: React.FC<{
  selectedJobConfig: ResumeScreeningConfig;
  setSelectedJobConfig: (selectedJobConfig: ResumeScreeningConfig) => void;
  greenhouseJobs: GreenhouseJob[];
  loadingGreenhouseJobs: boolean;
  jobStages: Record<string, string>;
  loadingJobStages: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  updateAllJobConfigs: (updatedConfig: ResumeScreeningConfig) => void;
  isImportGreenhouseJobOpen: boolean;
  toggleImportGreenhouseJobOpen: () => void;
  handleScreenTypeChange: (screenType: ScreenType) => void;
  isRecruitingScreeningEnabled: boolean;
}> = ({
  selectedJobConfig,
  setSelectedJobConfig,
  greenhouseJobs,
  loadingGreenhouseJobs,
  jobStages,
  loadingJobStages,
  handleInputChange,
  updateAllJobConfigs,
  isImportGreenhouseJobOpen,
  toggleImportGreenhouseJobOpen,
  handleScreenTypeChange,
  isRecruitingScreeningEnabled,
}) => {
  const podTypeOptions = [...Object.values(PodType).map(value => ({ id: value, name: value }))] as {
    id: PodType;
    name: PodType;
  }[];

  const [podType, setPodType] = useState<PodType>(selectedJobConfig?.podType ?? PodType.NotSet);

  useEffect(() => {
    setPodType(selectedJobConfig?.podType ?? PodType.NotSet);
  }, [selectedJobConfig?.podType]);

  const displayGreenhouseJobs: Record<string, GreenhouseJob> = useMemo(() => {
    return greenhouseJobs.reduce((newGreenhouseJobs, job) => {
      const key = `${job.name} (${job.id})`;
      newGreenhouseJobs[key] = job;
      return newGreenhouseJobs;
    }, {} as Record<string, GreenhouseJob>);
  }, [greenhouseJobs]);

  const displayJobStages: Record<string, string> = useMemo(() => {
    return jobStages
      ? Object.keys(jobStages).reduce((newJobStages, id) => {
          newJobStages[id] = `${jobStages[id]} (${id})`;
          return newJobStages;
        }, {} as Record<string, string>)
      : {};
  }, [jobStages]);

  const handleJobAutocompleteChange = (selectedOption: string) => {
    const selectedJob = displayGreenhouseJobs[selectedOption];
    if (selectedJob) {
      const updatedConfig: ResumeScreeningConfig = {
        ...selectedJobConfig,
        greenhouseJobId: selectedJob.id.toString(),
        greenhouseJobName: selectedJob.name,
      };
      setSelectedJobConfig(updatedConfig);
      updateAllJobConfigs(updatedConfig);
    }
  };

  const handleResumeReviewStageAutocompleteChange = (selectedOption: string) => {
    const stageId = Object.keys(displayJobStages).find(
      key => displayJobStages[key] === selectedOption,
    );
    if (stageId) {
      const updatedConfig = { ...selectedJobConfig, resumeReviewStageId: stageId };
      setSelectedJobConfig(updatedConfig);
      updateAllJobConfigs(updatedConfig);
    }
  };

  const handlePodChange = (newPodType: PodType) => {
    setPodType(newPodType);

    const updatedConfig: ResumeScreeningConfig = {
      ...selectedJobConfig,
      podType: newPodType,
    };
    setSelectedJobConfig(updatedConfig);
    updateAllJobConfigs(updatedConfig);
  };

  const getScreenTypeSelectionValue = () => {
    if (selectedJobConfig?.type === ScreenType.OCP) {
      return 'ocpResumeScreen';
    } else if (selectedJobConfig?.type === ScreenType.RecruitingGreenhouse) {
      return 'recruitingGreenhouseResumeScreen';
    } else {
      return 'greenhouseResumeScreen';
    }
  };

  return (
    <div>
      <div className="scaleui scale-theme scale-tailwind">
        <div onClick={toggleImportGreenhouseJobOpen} className="flex flex-row cursor-pointer">
          <Icon
            icon={['fas', 'chevron-right']}
            className={`text-neutral-800 transition-transform duration-300 ${
              isImportGreenhouseJobOpen ? 'rotate-90' : 'rotate-0'
            }`}
          />
          <Typography.H4 className="text-neutral-800">Import a Greenhouse job</Typography.H4>
        </div>
        <div className="p-2">
          {isImportGreenhouseJobOpen && (
            <div>
              <div className="flex flex-col mb-3">
                <Typography.Paragraph2 className="text-neutral-900 mb-1">
                  Screening Job Name
                </Typography.Paragraph2>
                <div className="text-neutral-500 text-xs mb-2">
                  We recommend using the name of the Greenhouse job listing.
                </div>
                <TextField
                  className="text-neutral-900 mb-3"
                  id="name"
                  value={selectedJobConfig?.name || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col mb-3">
                <Typography.Paragraph2 className="text-neutral-900 mb-1">
                  Resume Screening Type:
                </Typography.Paragraph2>
                <RadioGroup
                  value={getScreenTypeSelectionValue()}
                  onChange={(value: string) =>
                    handleScreenTypeChange(
                      value === 'greenhouseResumeScreen'
                        ? ScreenType.Greenhouse
                        : value === 'recruitingGreenhouseResumeScreen'
                        ? ScreenType.RecruitingGreenhouse
                        : ScreenType.OCP,
                    )
                  }
                >
                  <Radio
                    radioClassName="text-sm text-neutral-900 font-normal my-1 mx-2 p-0"
                    key="greenhouseResumeScreen"
                    value="greenhouseResumeScreen"
                    label={`Greenhouse ${isRecruitingScreeningEnabled ? '(Growth)' : ''}`}
                  />
                  {isRecruitingScreeningEnabled && (
                    <Radio
                      radioClassName="text-sm text-neutral-900 font-normal my-1 mx-2 p-0"
                      key="recruitingGreenhouseResumeScreen"
                      value="recruitingGreenhouseResumeScreen"
                      label="Greenhouse (Recruiting)"
                    />
                  )}
                  <Radio
                    radioClassName="text-sm text-neutral-900 font-normal my-1 mx-2 p-0"
                    key="ocpResumeScreen"
                    value="ocpResumeScreen"
                    label="OCP"
                  />
                </RadioGroup>
              </div>
              {selectedJobConfig.type !== ScreenType.OCP && (
                <>
                  <div className="flex flex-row justify-between mb-3">
                    <div className="flex flex-col">
                      <Typography.Paragraph2 className="text-neutral-900 mb-3">
                        Greenhouse Job
                      </Typography.Paragraph2>
                      <Autocomplete
                        className="w-auto h-auto mb-3"
                        canCreateOption={false}
                        options={Object.keys(displayGreenhouseJobs)}
                        value={
                          loadingGreenhouseJobs
                            ? 'Loading...'
                            : selectedJobConfig?.greenhouseJobName
                        }
                        setSelectedOption={handleJobAutocompleteChange}
                        disabled={loadingGreenhouseJobs}
                        optionClassName="truncate"
                        trailingIcon={'chevron-down'}
                      />
                    </div>
                    {selectedJobConfig._id && (
                      <Button
                        className="mb-3"
                        size="sm"
                        variant="neutral"
                        onClick={() => screenGreenhouseJobBacklog(selectedJobConfig._id)}
                      >
                        Screen backlog
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-col mb-3">
                    <Typography.Paragraph2 className="text-neutral-900 mb-3">
                      Resume Review Stage Name
                    </Typography.Paragraph2>
                    <Autocomplete
                      className="w-full h-auto mb-3"
                      canCreateOption={false}
                      options={Object.values(displayJobStages)}
                      value={
                        loadingJobStages || !jobStages
                          ? 'Loading...'
                          : displayJobStages[selectedJobConfig?.resumeReviewStageId]
                      }
                      setSelectedOption={handleResumeReviewStageAutocompleteChange}
                      disabled={loadingGreenhouseJobs || loadingJobStages || _.isEmpty(jobStages)}
                      trailingIcon={'chevron-down'}
                    />
                  </div>
                </>
              )}
              <div className="flex flex-col mb-3">
                <div className="flex flex-row mb-3">
                  <div className="text-black">Pod Type</div>
                  <div className="bg-neutral-200 rounded-lg text-neutral-800 font-medium text-xs px-1 mx-2 whitespace-nowrap">
                    optional
                  </div>
                </div>
                <SelectMenu
                  options={podTypeOptions}
                  selectedOptionId={podType}
                  setSelected={handlePodChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
